import { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "@clerk/react-router";
import { AlertCircle } from "@untitledui/icons";
import { Accordion } from "@/components/application/accordion/accordion";
import { FileUpload } from "@/components/application/file-upload/file-upload-base";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { UploadStatusCard } from "@/components/application/upload-status/upload-status-card";
import type { UploadStatus } from "@/components/application/upload-status/upload-status-card";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { stateGroups } from "@/config/upload-states";
import { useBridgeHealth } from "@/hooks/use-bridge-health";
import { useClientBridge } from "@/hooks/use-client-bridge";
import { useFilesPolling } from "@/hooks/use-files-polling";
import { getDocumentStatusLabel } from "@/lib/status-mapping";
import { useFilesApi } from "@/services/api/files-api";
import type { DocumentDisplayData, FileStorageState } from "@/types/upload-file";

/**
 * Legacy Home Page — upload list and status (route /legacy-home).
 */

// =============================================================================
// HELPERS
// =============================================================================

function getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
}

// =============================================================================
// DISPLAY ITEM TYPE
// =============================================================================

interface DisplayItem {
    id: string;
    type: "file" | "document";
    fileName: string;
    /** Auto-generated document name to display instead of filename */
    generatedName?: string;
    fileSizeBytes: number;
    pageCount?: number;
    uiStatus: UploadStatus;
    progress: number;
    statusLabel: string;
    documentType?: string;
    document?: {
        id: number;
        vendor?: string;
        date?: string;
        amount?: string;
        description?: string;
        documentNumber?: string;
    };
    errorCode?: string;
    errorMessage?: string;
    hasRetryWarning?: boolean;
    createdAt: Date;
    originalFileId: string;
    documentId?: number;
    fileStorageId?: number;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function LegacyHomePage() {
    const [greeting, setGreeting] = useState<string>("Hello");
    const [canUpload, setCanUpload] = useState<boolean>(false);
    const [isCheckingUpload, setIsCheckingUpload] = useState<boolean>(true);

    // Get user info from Clerk
    const { user } = useUser();
    const firstName = user?.firstName || "";

    // Use custom hook for file management and polling
    const {
        files,
        isLoading,
        blocker,
        uploadFiles,
        deleteFile,
        deleteDocument,
        fetchExistingFiles,
        calculateDocumentProgress,
        getDocumentUiStatus,
        seenStepCodesRef,
    } = useFilesPolling();

    // Files API for checking upload availability
    const filesApi = useFilesApi();

    // Bridge health status (only poll when client has a bridge)
    const { hasBridge } = useClientBridge();
    const { status: bridgeStatus } = useBridgeHealth(hasBridge);
    const isBridgeDead = hasBridge && bridgeStatus === "dead";

    // Set greeting only on client side
    useEffect(() => {
        setGreeting(getGreeting());
    }, []);

    // Check upload availability on mount
    useEffect(() => {
        const checkUploadAvailability = async () => {
            try {
                setIsCheckingUpload(true);
                const response = await filesApi.canUpload();
                setCanUpload(response.canUpload);
            } catch (error) {
                console.error("Failed to check upload availability:", error);
                setCanUpload(false);
            } finally {
                setIsCheckingUpload(false);
            }
        };

        checkUploadAvailability();
    }, [filesApi]);

    // Fetch existing files on mount
    useEffect(() => {
        fetchExistingFiles();
    }, [fetchExistingFiles]);

    const handleCancel = useCallback(
        (item: DisplayItem) => {
            if (item.type === "document" && item.documentId) {
                deleteDocument(item.documentId);
            } else {
                deleteFile(item.originalFileId);
            }
        },
        [deleteDocument, deleteFile],
    );

    const handleRemove = useCallback(
        (item: DisplayItem) => {
            if (item.type === "document" && item.documentId) {
                deleteDocument(item.documentId);
            } else {
                deleteFile(item.originalFileId);
            }
        },
        [deleteDocument, deleteFile],
    );

    const handleViewFile = useCallback((fileStorageId?: number) => {
        if (fileStorageId) {
            window.open(`/legacy-files?id=${fileStorageId}`, "_blank");
        }
    }, []);

    // Flatten files into display items (Files or Documents)
    const flattenedItems = useMemo(() => {
        const items: DisplayItem[] = [];

        files.forEach((file: FileStorageState) => {
            // If file has documents, show document cards for inbound documents
            if (file.showDocumentCards && file.documents.length > 0) {
                file.documents.forEach((doc: DocumentDisplayData) => {
                    // Only show upload status card for inbound documents
                    if (doc.isInbound !== true) return;

                    // Get dynamic status label based on processing state
                    const statusLabel = getDocumentStatusLabel(doc.status, doc.isProcessingEndStateReached, doc.currentStepCode);

                    // Calculate progress and UI status from the hook's helpers
                    const seenCodes = seenStepCodesRef.current.get(doc.id) || new Set<string>();
                    const progress = calculateDocumentProgress(doc, seenCodes);
                    const uiStatus = getDocumentUiStatus(doc);

                    items.push({
                        id: `doc-${doc.id}`,
                        type: "document",
                        fileName: file.fileName,
                        generatedName: doc.generatedName,
                        fileSizeBytes: file.fileSizeBytes,
                        pageCount: file.pageCount,
                        uiStatus,
                        progress,
                        statusLabel,
                        documentType: doc.documentType,
                        document: {
                            id: doc.id,
                            vendor: doc.vendor,
                            date: doc.date,
                            amount: doc.amount,
                            description: doc.description,
                            documentNumber: doc.documentNumber,
                        },
                        errorCode: doc.errorCode,
                        errorMessage: doc.errorMessage,
                        hasRetryWarning: doc.hasRetryWarning,
                        createdAt: new Date(doc.createdAt),
                        originalFileId: file.id,
                        documentId: doc.id,
                        fileStorageId: file.fileStorageId,
                    });
                });
            } else if (!file.showDocumentCards) {
                // Show file card only when no documents to show
                items.push({
                    id: file.id,
                    type: "file",
                    fileName: file.fileName,
                    fileSizeBytes: file.fileSizeBytes,
                    pageCount: file.pageCount,
                    uiStatus: file.uiStatus,
                    progress: file.progress,
                    statusLabel: file.statusLabel,
                    errorCode: file.errorCode,
                    errorMessage: file.errorMessage,
                    hasRetryWarning: file.hasRetryWarning,
                    createdAt: file.createdAt,
                    originalFileId: file.id,
                    fileStorageId: file.fileStorageId,
                });
            }
        });
        return items;
    }, [files, calculateDocumentProgress, getDocumentUiStatus, seenStepCodesRef]);

    // Group and sort files by state
    const groupedFiles = useMemo(() => {
        const groups: Map<string, DisplayItem[]> = new Map();

        stateGroups.forEach((group) => {
            groups.set(group.id, []);
        });

        flattenedItems.forEach((item) => {
            const group = stateGroups.find((g) => g.statuses.includes(item.uiStatus));
            if (group) {
                const groupFiles = groups.get(group.id) || [];
                groupFiles.push(item);
                groups.set(group.id, groupFiles);
            } else {
                // Fallback for unknown statuses
                const activeGroup = groups.get("active");
                activeGroup?.push(item);
            }
        });

        // Sort files within each group by createdAt (newest first)
        groups.forEach((groupFiles, groupId) => {
            groupFiles.sort((a, b) => {
                const aTime = a.createdAt?.getTime() ?? 0;
                const bTime = b.createdAt?.getTime() ?? 0;
                return bTime - aTime;
            });
            groups.set(groupId, groupFiles);
        });

        return groups;
    }, [flattenedItems]);

    // Get groups with files, sorted by priority
    const activeGroups = useMemo(() => {
        return stateGroups.filter((group) => (groupedFiles.get(group.id) || []).length > 0).sort((a, b) => b.priority - a.priority);
    }, [groupedFiles]);

    return (
        <div className="h-full overflow-auto bg-primary">
            {/* Navigation Blocker Modal */}
            {blocker.state === "blocked" && (
                <ModalOverlay isOpen isDismissable={false}>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-xl">
                                <h2 className="mb-2 text-lg font-semibold text-primary">Upload in Progress</h2>
                                <p className="mb-6 text-tertiary">
                                    Files are still being uploaded. Are you sure you want to leave this page? Your uploads may be interrupted.
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button color="secondary" size="md" onClick={() => blocker.reset?.()}>
                                        Stay on Page
                                    </Button>
                                    <Button color="primary-destructive" size="md" onClick={() => blocker.proceed?.()}>
                                        Leave Anyway
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            <div className="container mx-auto max-w-4xl p-6">
                {/* Greeting Section */}
                <div className="mb-8">
                    <h1 className="mb-2 text-3xl font-semibold text-secondary">
                        {greeting}
                        {firstName ? `, ${firstName}` : ""}
                    </h1>
                    <p className="text-tertiary">Upload your documents to get started with automated document processing.</p>
                </div>

                {/* Upload Area */}
                <FileUpload.Root>
                    <FileUpload.DropZone
                        hint={
                            isBridgeDead
                                ? "Upload unavailable - bridge is disconnected"
                                : isCheckingUpload
                                  ? "Checking upload availability..."
                                  : canUpload
                                    ? "PDF and image files accepted up to 50MB"
                                    : "Upload unavailable - quota limit reached"
                        }
                        maxSize={50 * 1024 * 1024}
                        allowsMultiple={true}
                        accept="image/*,application/pdf"
                        isDisabled={!canUpload || isCheckingUpload || isBridgeDead}
                        onDropFiles={uploadFiles}
                        onDropUnacceptedFiles={(rejectedFiles) => {
                            console.log("Rejected files:", rejectedFiles);
                        }}
                        onSizeLimitExceed={(oversizedFiles) => {
                            console.log("Oversized files:", oversizedFiles);
                        }}
                    />
                </FileUpload.Root>

                {/* Bridge Down Warning */}
                {isBridgeDead && (
                    <div className="mt-6 flex items-center justify-center gap-3 rounded-xl bg-error-solid px-4 py-3.5">
                        <AlertCircle className="size-5 shrink-0 text-white" />
                        <p className="text-sm font-semibold tracking-wide text-white uppercase">The bridge is down. Please contact system administrator</p>
                    </div>
                )}

                {/* Loading State */}
                {isLoading && activeGroups.length == 0 && <div className="mt-8 text-center text-tertiary">Loading existing files...</div>}

                {/* Files by State Group */}
                {activeGroups.length > 0 && (
                    <div className="mt-8">
                        <Accordion.Root
                            allowMultiple
                            persistKey="upload-status-groups"
                            className="space-y-2"
                            defaultExpanded={["active", "needs-review", "errors"]}
                        >
                            {activeGroups.map((group) => {
                                const groupFiles = groupedFiles.get(group.id) || [];

                                return (
                                    <Accordion.Item key={group.id} id={group.id} itemCount={groupFiles.length}>
                                        <Accordion.Trigger
                                            badge={
                                                <Badge size="sm" color={group.badgeColor} type="color">
                                                    {groupFiles.length}
                                                </Badge>
                                            }
                                        >
                                            {group.label}
                                        </Accordion.Trigger>
                                        <Accordion.Content>
                                            <div className="space-y-4 pt-2">
                                                {groupFiles.map((item) => (
                                                    <UploadStatusCard
                                                        key={item.id}
                                                        filename={item.fileName}
                                                        generatedName={item.generatedName}
                                                        status={item.uiStatus}
                                                        progress={item.progress}
                                                        statusLabel={item.statusLabel}
                                                        pageCount={item.pageCount}
                                                        fileSize={item.fileSizeBytes}
                                                        documentType={item.documentType}
                                                        document={item.document}
                                                        errorCode={item.errorCode}
                                                        errorMessage={item.errorMessage}
                                                        hasRetryWarning={item.hasRetryWarning}
                                                        showProgress={group.id === "active" ? !isBridgeDead : true}
                                                        onCancel={() => handleCancel(item)}
                                                        onRemove={() => handleRemove(item)}
                                                        onViewFile={() => handleViewFile(item.fileStorageId)}
                                                        onGoToDocument={
                                                            item.documentId
                                                                ? () => {
                                                                      window.location.href = `/legacy-documents?id=${item.documentId}`;
                                                                  }
                                                                : undefined
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        </Accordion.Content>
                                    </Accordion.Item>
                                );
                            })}
                        </Accordion.Root>
                    </div>
                )}
            </div>
        </div>
    );
}
